import { invoke, isTauri } from '@tauri-apps/api/core';
import { appMessage as message } from '@utils/appMessage';

export interface SaveFileResult {
  success: boolean;
  path?: string;
  message: string;
}

function isTauriEnvironment(): boolean {
  return isTauri();
}

function getPlatform(): 'desktop' | 'mobile' | 'web' {
  if (!isTauriEnvironment()) {
    return 'web';
  }
  
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes('android') || userAgent.includes('iphone') || userAgent.includes('ipad')) {
    return 'mobile';
  }
  return 'desktop';
}

/** 是否为移动端 Tauri 环境（Android/iOS WebView）。 */
export function isMobilePlatform(): boolean {
  return getPlatform() === 'mobile';
}

/** 判断是否为 OSS key（非完整 URL：非 http/data/asset/file 开头的存储 key）。 */
export function isOssKey(value: string): boolean {
  return (
    !value.startsWith('http') &&
    !value.startsWith('data:') &&
    !value.startsWith('asset://') &&
    !value.startsWith('file://')
  );
}

/**
 * ★ 保存云存储附件到设备下载目录（移动端专用）
 * 直接调用 Rust 侧 `save_oss_attachment_to_downloads`：在 Rust 中经服务端签名 URL
 * 从七牛下载，完全绕过浏览器 WebView 的 CORS 限制（Android WebView 源
 * http://tauri.localhost 不被存储桶 CORS 放行），
 * Android 再经 MediaStore 写入系统下载目录。
 * @param ossKey 对象存储 key（如 users/{userId}/attachments/{hash}.{ext}）
 * @param filename 保存的文件名
 */
export async function saveOssAttachment(ossKey: string, filename: string): Promise<SaveFileResult> {
  try {
    const result = await invoke<SaveFileResult>('save_oss_attachment_to_downloads', {
      ossKey,
      filename,
    });
    return result;
  } catch (error) {
    console.error('[FileSave] OSS 附件保存失败:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : '保存文件失败',
    };
  }
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * 保存附件文件（跨平台）
 * @param fileSource 文件源（URL、Blob 或 Base64 字符串）
 * @param filename 默认文件名
 * @returns 保存结果
 */
export async function saveFile(
  fileSource: string | Blob,
  filename: string
): Promise<SaveFileResult> {
  const platform = getPlatform();
  
  console.log(`[FileSave] 平台: ${platform}, 文件名: ${filename}`);
  
  if (platform === 'web') {
    return saveFileWeb(fileSource, filename);
  }
  
  if (platform === 'mobile') {
    return saveFileMobile(fileSource, filename);
  }
  
  return saveFileDesktop(fileSource, filename);
}

async function saveFileDesktop(
  fileSource: string | Blob,
  filename: string
): Promise<SaveFileResult> {
  try {
    let fileData: Uint8Array;
    
    if (typeof fileSource === 'string') {
      if (fileSource.startsWith('data:')) {
        // Base64 数据
        const base64Data = fileSource.split(',')[1];
        fileData = base64ToUint8Array(base64Data);
      } else if (fileSource.startsWith('asset://') || fileSource.startsWith('file://')) {
        // Tauri 本地文件路径
        const result: SaveFileResult = await invoke('save_attachment_from_path', {
          filePath: fileSource,
          defaultFilename: filename,
        });
        return result;
      } else {
        // 网络 URL
        const response = await fetch(fileSource);
        const blob = await response.blob();
        fileData = new Uint8Array(await blob.arrayBuffer());
      }
    } else {
      // Blob
      fileData = new Uint8Array(await fileSource.arrayBuffer());
    }
    
    const result: SaveFileResult = await invoke('save_attachment_file', {
      fileData: Array.from(fileData),
      defaultFilename: filename,
    });
    
    return result;
  } catch (error) {
    console.error('[FileSave] 桌面端保存失败:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : '保存文件失败',
    };
  }
}

async function saveFileMobile(
  fileSource: string | Blob,
  filename: string
): Promise<SaveFileResult> {
  try {
    let fileData: Uint8Array;
    
    if (typeof fileSource === 'string') {
      if (fileSource.startsWith('data:')) {
        const base64Data = fileSource.split(',')[1];
        fileData = base64ToUint8Array(base64Data);
      } else {
        const response = await fetch(fileSource);
        const blob = await response.blob();
        fileData = new Uint8Array(await blob.arrayBuffer());
      }
    } else {
      fileData = new Uint8Array(await fileSource.arrayBuffer());
    }
    
    // 移动端使用保存到下载目录的命令
    const result: SaveFileResult = await invoke('save_file_to_downloads', {
      fileData: Array.from(fileData),
      filename: filename,
    });
    
    if (result.success && result.path) {
      message.success(`附件已保存到: ${result.path}`);
    }
    
    return result;
  } catch (error) {
    console.error('[FileSave] 移动端保存失败:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : '保存文件失败',
    };
  }
}

function saveFileWeb(
  fileSource: string | Blob,
  filename: string
): Promise<SaveFileResult> {
  return new Promise((resolve) => {
    try {
      let url: string;
      
      if (typeof fileSource === 'string') {
        url = fileSource;
      } else {
        url = URL.createObjectURL(fileSource);
      }
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      if (typeof fileSource !== 'string') {
        URL.revokeObjectURL(url);
      }
      
      resolve({
        success: true,
        message: '文件下载已开始',
      });
    } catch (error) {
      console.error('[FileSave] Web 端保存失败:', error);
      resolve({
        success: false,
        message: error instanceof Error ? error.message : '保存文件失败',
      });
    }
  });
}
